import {
  ArrowArcLeft,
  ArrowArcRight,
  CaretLeft,
  CaretRight,
  Pause,
  Play,
  Scissors,
  SpeakerHigh,
  SpeakerSlash,
  TextT,
  Trash,
} from '@phosphor-icons/react';
import {
  type ReelTimeline,
  retimeWords,
  type SpokenWord,
  subtitleCues,
} from '@reelplicate/flows';
import { cn, IconButton, Inline, Slider, Stack, Tooltip } from '@reelplicate/ui';
import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react';

import {
  formatTime,
  placedClips,
  type Selection,
  type TimelineAction,
} from './timeline-state';
import type { Playback } from './use-playback';

/** Pixels per second the timeline opens at, and how far it zooms. */
const ZOOM = { initial: 36, min: 12, max: 160 };
/** How far a press has to move before it's a drag rather than a click. */
const DRAG_THRESHOLD = 4;

type Drag = (seconds: number) => void;

/**
 * Follows a pointer from a press: `onMove` gets how far it has gone, in
 * timeline seconds. The press is captured, so the drag keeps going outside
 * the element.
 */
function follow(
  event: ReactPointerEvent<HTMLElement>,
  pxPerSecond: number,
  onMove: Drag,
  onEnd?: (moved: boolean) => void,
) {
  event.stopPropagation();
  const target = event.currentTarget;
  target.setPointerCapture?.(event.pointerId);
  const startX = event.clientX;
  let moved = false;
  const move = (e: PointerEvent) => {
    if (!moved && Math.abs(e.clientX - startX) < DRAG_THRESHOLD) return;
    moved = true;
    onMove((e.clientX - startX) / pxPerSecond);
  };
  const end = () => {
    target.removeEventListener('pointermove', move);
    target.removeEventListener('pointerup', end);
    target.removeEventListener('pointercancel', end);
    onEnd?.(moved);
  };
  target.addEventListener('pointermove', move);
  target.addEventListener('pointerup', end);
  target.addEventListener('pointercancel', end);
}

/**
 * The edit as tracks under the preview: the cuts of the reel in order (drag
 * to reorder, drag an edge to trim), text over them, and the subtitles the
 * voiceover gives. A toolbar acts on the selection and the playhead.
 */
export function TimelinePanel({
  timeline,
  selected,
  canUndo,
  canRedo,
  words,
  playback,
  dispatch,
  onAddText,
  actions,
}: {
  timeline: ReelTimeline;
  selected: Selection;
  canUndo: boolean;
  canRedo: boolean;
  words: readonly SpokenWord[];
  playback: Playback;
  dispatch: (action: TimelineAction) => void;
  onAddText: () => void;
  /** Save, Discard and the render status, at the end of the toolbar. */
  actions: ReactNode;
}) {
  const [pxPerSecond, setPxPerSecond] = useState(ZOOM.initial);
  const { time, duration, playing, toggle, seek } = playback;
  const placed = placedClips(timeline);
  const selectedClip =
    selected?.kind === 'clip' ? placed.find((p) => p.clip.id === selected.id) : undefined;
  const width = Math.max(duration * pxPerSecond, 1);
  const x = (seconds: number) => seconds * pxPerSecond;

  // One block per stretch of subtitle on screen (word mode re-draws it per word).
  const cues = useMemo(() => {
    if (!timeline.subtitles?.enabled || !words.length) return [];
    const blocks: { text: string; start: number; end: number }[] = [];
    for (const cue of subtitleCues(
      retimeWords(words, timeline.clips),
      timeline.subtitles.mode,
    )) {
      const text = cue.words.join(' ');
      const last = blocks.at(-1);
      if (last?.text === text && cue.highlight !== 0) last.end = cue.end;
      else blocks.push({ text, start: cue.start, end: cue.end });
    }
    return blocks;
  }, [timeline.subtitles, timeline.clips, words]);

  const canSplit = (() => {
    const at = placed.find((p) => time >= p.start && time < p.start + p.length);
    return Boolean(at && time - at.start >= 0.3 && at.start + at.length - time >= 0.3);
  })();

  const select = (selection: Selection) => dispatch({ type: 'select', selection });

  /** Press on the ruler or an empty track: move the playhead there, and scrub. */
  const scrub = (event: ReactPointerEvent<HTMLElement>) => {
    const left = event.currentTarget.getBoundingClientRect().left;
    const at = (event.clientX - left) / pxPerSecond;
    seek(at);
    select(null);
    follow(event, pxPerSecond, (dx) => seek(at + dx));
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, [role="slider"]')) return;
    const mod = event.metaKey || event.ctrlKey;
    if (mod && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
    } else if (event.key === ' ' && !target.closest('button')) {
      event.preventDefault();
      toggle();
    } else if (event.key === 's' && !mod) {
      dispatch({ type: 'split', at: time });
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && selected) {
      event.preventDefault();
      dispatch(
        selected.kind === 'clip'
          ? { type: 'removeClip', id: selected.id }
          : { type: 'removeText', id: selected.id },
      );
    }
  };

  return (
    <Stack gap={2} className="min-w-0" onKeyDown={onKeyDown}>
      <Inline gap={1} alignY="center" wrap aria-label="Timeline tools" role="toolbar">
        <IconButton
          icon={playing ? Pause : Play}
          size="sm"
          variant="outline"
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={toggle}
        />
        <span className="text-caption text-text-secondary min-w-24 px-1 tabular-nums">
          {formatTime(time)} / {formatTime(duration)}
        </span>
        <Tool
          label="Split at the playhead (S)"
          icon={Scissors}
          disabled={!canSplit}
          onClick={() => dispatch({ type: 'split', at: time })}
        />
        <Tool
          label="Move clip earlier"
          icon={CaretLeft}
          disabled={!selectedClip || selectedClip.index === 0}
          onClick={() =>
            selectedClip &&
            dispatch({
              type: 'move',
              id: selectedClip.clip.id,
              to: selectedClip.index - 1,
            })
          }
        />
        <Tool
          label="Move clip later"
          icon={CaretRight}
          disabled={!selectedClip || selectedClip.index === placed.length - 1}
          onClick={() =>
            selectedClip &&
            dispatch({
              type: 'move',
              id: selectedClip.clip.id,
              to: selectedClip.index + 1,
            })
          }
        />
        <Tool
          label={selectedClip?.clip.volume === 0 ? 'Unmute clip' : 'Mute clip'}
          icon={selectedClip?.clip.volume === 0 ? SpeakerSlash : SpeakerHigh}
          disabled={!selectedClip}
          onClick={() =>
            selectedClip &&
            dispatch({
              type: 'setVolume',
              id: selectedClip.clip.id,
              volume: selectedClip.clip.volume === 0 ? 1 : 0,
            })
          }
        />
        <Tool
          label={selected?.kind === 'text' ? 'Delete text' : 'Delete clip'}
          icon={Trash}
          disabled={!selected || (selected.kind === 'clip' && placed.length <= 1)}
          onClick={() =>
            selected &&
            dispatch(
              selected.kind === 'clip'
                ? { type: 'removeClip', id: selected.id }
                : { type: 'removeText', id: selected.id },
            )
          }
        />
        <Tool label="Add text at the playhead" icon={TextT} onClick={onAddText} />
        <Tool
          label="Undo (⌘Z)"
          icon={ArrowArcLeft}
          disabled={!canUndo}
          onClick={() => dispatch({ type: 'undo' })}
        />
        <Tool
          label="Redo (⇧⌘Z)"
          icon={ArrowArcRight}
          disabled={!canRedo}
          onClick={() => dispatch({ type: 'redo' })}
        />
        <div className="ml-auto">
          <Inline gap={2} alignY="center">
            {actions}
          </Inline>
        </div>
      </Inline>

      <div className="border-border-subtle bg-bg-subtle overflow-x-auto rounded-md border">
        <div className="relative" style={{ width: width + 24 }}>
          <div className="relative ml-3" style={{ width }}>
            {/* Ruler: press to move the playhead. */}
            <div
              aria-label="Seek"
              role="presentation"
              className="text-caption text-text-tertiary relative h-6 cursor-pointer select-none"
              onPointerDown={scrub}
            >
              {Array.from(
                { length: Math.floor(duration / tick(pxPerSecond)) + 1 },
                (_, i) => {
                  const at = i * tick(pxPerSecond);
                  return (
                    <span
                      key={i}
                      className="border-border-default absolute top-0 h-full border-l pl-1 leading-6 tabular-nums"
                      style={{ left: x(at) }}
                    >
                      {formatTime(at).replace(/\.0$/, '')}
                    </span>
                  );
                },
              )}
            </div>

            {/* Clips */}
            <Lane label="Video" onPointerDown={scrub}>
              {placed.map(({ clip, index, start, length }) => {
                const isSelected = selected?.kind === 'clip' && selected.id === clip.id;
                return (
                  <button
                    key={clip.id}
                    type="button"
                    aria-label={`Clip ${index + 1}, ${formatTime(clip.in)} to ${formatTime(clip.out)} of the reel${clip.volume === 0 ? ', muted' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => select({ kind: 'clip', id: clip.id })}
                    onPointerDown={(event) => {
                      const left = event.currentTarget.getBoundingClientRect().left;
                      seek(start + (event.clientX - left) / pxPerSecond);
                      select({ kind: 'clip', id: clip.id });
                      // Drag to reorder: the clip goes where its middle lands.
                      const others = placed.filter((p) => p.clip.id !== clip.id);
                      let checkpointed = false;
                      follow(event, pxPerSecond, (dx) => {
                        if (!checkpointed) {
                          dispatch({ type: 'checkpoint' });
                          checkpointed = true;
                        }
                        const middle = start + length / 2 + dx;
                        const to = others.filter(
                          (p) => p.start + p.length / 2 < middle,
                        ).length;
                        dispatch({ type: 'move', id: clip.id, to, transient: true });
                      });
                    }}
                    className={cn(
                      'focus-visible:shadow-focus-ring group absolute top-1 bottom-1 overflow-hidden rounded-sm border text-left outline-none',
                      'bg-brand-subtle border-border-brand/40 touch-none',
                      isSelected && 'ring-border-brand ring-2',
                    )}
                    style={{ left: x(start), width: Math.max(x(length) - 2, 4) }}
                  >
                    <span className="text-caption text-text-primary block truncate px-2 leading-8">
                      {index + 1}
                      {clip.volume === 0 && ' · muted'}
                    </span>
                    {isSelected && (
                      <>
                        <Handle
                          side="left"
                          label="Trim the start"
                          onPointerDown={(event) => {
                            dispatch({ type: 'checkpoint' });
                            const base = clip.in;
                            follow(event, pxPerSecond, (dx) =>
                              dispatch({
                                type: 'trim',
                                id: clip.id,
                                edge: 'in',
                                to: base + dx,
                                transient: true,
                              }),
                            );
                          }}
                        />
                        <Handle
                          side="right"
                          label="Trim the end"
                          onPointerDown={(event) => {
                            dispatch({ type: 'checkpoint' });
                            const base = clip.out;
                            follow(event, pxPerSecond, (dx) =>
                              dispatch({
                                type: 'trim',
                                id: clip.id,
                                edge: 'out',
                                to: base + dx,
                                transient: true,
                              }),
                            );
                          }}
                        />
                      </>
                    )}
                  </button>
                );
              })}
            </Lane>

            {/* Text */}
            <Lane label="Text" onPointerDown={scrub}>
              {timeline.texts.map((text) => {
                const isSelected = selected?.kind === 'text' && selected.id === text.id;
                const moveBy = (dx: number, edge?: 'start' | 'end') => {
                  if (edge === 'start') return { start: text.start + dx };
                  if (edge === 'end') return { end: text.end + dx };
                  const shift = Math.min(Math.max(dx, -text.start), duration - text.end);
                  return { start: text.start + shift, end: text.end + shift };
                };
                const drag =
                  (edge?: 'start' | 'end') => (event: ReactPointerEvent<HTMLElement>) => {
                    select({ kind: 'text', id: text.id });
                    let checkpointed = false;
                    follow(event, pxPerSecond, (dx) => {
                      if (!checkpointed) {
                        dispatch({ type: 'checkpoint' });
                        checkpointed = true;
                      }
                      dispatch({
                        type: 'updateText',
                        id: text.id,
                        patch: moveBy(dx, edge),
                        transient: true,
                      });
                    });
                  };
                return (
                  <button
                    key={text.id}
                    type="button"
                    aria-label={`Text “${text.text}”, ${formatTime(text.start)} to ${formatTime(text.end)}`}
                    aria-pressed={isSelected}
                    onClick={() => select({ kind: 'text', id: text.id })}
                    onPointerDown={drag()}
                    className={cn(
                      'focus-visible:shadow-focus-ring absolute top-1 bottom-1 touch-none overflow-hidden rounded-sm border text-left outline-none',
                      'bg-status-warning-bg border-status-warning-solid/40',
                      isSelected && 'ring-border-brand ring-2',
                    )}
                    style={{
                      left: x(text.start),
                      width: Math.max(x(text.end - text.start) - 2, 4),
                    }}
                  >
                    <span className="text-caption text-text-primary block truncate px-2 leading-8">
                      {text.text.replaceAll('*', '')}
                    </span>
                    {isSelected && (
                      <>
                        <Handle
                          side="left"
                          label="Change when the text starts"
                          onPointerDown={drag('start')}
                        />
                        <Handle
                          side="right"
                          label="Change when the text ends"
                          onPointerDown={drag('end')}
                        />
                      </>
                    )}
                  </button>
                );
              })}
            </Lane>

            {/* Subtitles: read-only, from the voiceover. */}
            {cues.length > 0 && (
              <Lane label="Subtitles" onPointerDown={scrub} short>
                {cues.map((cue) => (
                  <span
                    key={`${cue.start}-${cue.text}`}
                    title={cue.text}
                    className="bg-bg-muted text-caption text-text-secondary absolute top-1 bottom-1 truncate rounded-xs px-1 leading-5"
                    style={{
                      left: x(cue.start),
                      width: Math.max(x(cue.end - cue.start) - 1, 2),
                    }}
                  >
                    {cue.text}
                  </span>
                ))}
              </Lane>
            )}

            {/* Playhead */}
            <span
              aria-hidden
              className="bg-status-danger-solid pointer-events-none absolute top-0 bottom-0 w-0.5"
              style={{ left: x(time) }}
            />
          </div>
        </div>
      </div>

      <Inline gap={2} alignY="center" className="self-end">
        <span className="text-caption text-text-tertiary">Zoom</span>
        <Slider
          aria-label="Zoom"
          className="w-32"
          min={ZOOM.min}
          max={ZOOM.max}
          value={[pxPerSecond]}
          showTooltip={false}
          onValueChange={([value]) => value !== undefined && setPxPerSecond(value)}
        />
      </Inline>
    </Stack>
  );
}

/** A ruler mark every so many seconds, so labels don't crowd. */
const tick = (pxPerSecond: number) =>
  [1, 2, 5, 10, 15, 30].find((s) => s * pxPerSecond >= 56) ?? 60;

function Lane({
  label,
  short = false,
  onPointerDown,
  children,
}: {
  label: string;
  short?: boolean;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  children: ReactNode;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('border-border-subtle relative border-t', short ? 'h-7' : 'h-10')}
      onPointerDown={onPointerDown}
    >
      {children}
    </div>
  );
}

function Handle({
  side,
  label,
  onPointerDown,
}: {
  side: 'left' | 'right';
  label: string;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <span
      aria-label={label}
      role="presentation"
      onPointerDown={onPointerDown}
      className={cn(
        'bg-border-brand absolute top-0 bottom-0 w-2.5 cursor-ew-resize touch-none',
        side === 'left' ? 'left-0 rounded-l-sm' : 'right-0 rounded-r-sm',
      )}
    />
  );
}

function Tool({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: typeof Play;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip content={label}>
      <IconButton
        icon={icon}
        size="sm"
        variant="ghost"
        aria-label={label.replace(/ \(.*\)$/, '')}
        disabled={disabled}
        onClick={onClick}
      />
    </Tooltip>
  );
}
